// Administrador Master → crédito y consumo, configuración (ADR-0047). Consumption = billable impressions
// accepted by the ad server (confirmed platform reviews excluded, ADR-0046) × CPM in force. Invoiced,
// paid and overdue amounts do not exist until the Billing checkpoint: they are shown as unavailable.
import { NOTIFICATION_EVENTS, OPERATOR_NOTIFICATION_EVENTS, type NotificationEvent } from "@interactads/domain";
import { useState, type SyntheticEvent } from "react";
import { toast } from "sonner";
import { ApiProblem } from "@/api/errors";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Field } from "@/ui/field";
import { Input } from "@/ui/input";
import { Callout, EmptyState, PageHeader, Section, Table, Td, Th } from "@/ui/layout";
import { formatDateTime } from "@/lib/format";
import { PageError, PageLoading, useTitle } from "../common/page";
import { PAYMENT_MODE_LABELS, ruleMessage, usd, formField } from "../commercial/labels";
import { adminPost, useAdmin, useAdminMutation } from "./admin-api";
import { useOperatorRoles } from "./admin-layout";
import { CreditControlPanel } from "./admin-credit-control";

interface CreditRow {
  organizationId: string;
  legalName: string | null;
  paymentMode: string;
  creditLimitMicros: number;
  committedMicros: number;
  availableMicros: number;
  paymentTermsDays: number;
  isTest: boolean | null;
}
interface Line {
  key: string;
  label: string;
  impressions: number;
  valueMicros: number | null;
  approvedBudgetMicros: number | null;
}
interface Consumption {
  available: boolean;
  /** When the report shown was measured (a finished run), and how far a newer one is (report 106). */
  measuredAt: string | null;
  run: null | { status: "RUNNING" | "COMPLETED"; distributions: number };
  report: null | {
    /** null when some company could not be measured (never a partial total). */
    impressions: number | null;
    valueMicros: number | null;
    complete: boolean;
    unmeasuredOrganizations: string[];
    byOrganization: Line[];
    byClient: Line[];
    byCampaign: Line[];
    byPurchaseOrder: Line[];
    exhausted: Array<{ campaignId: string; consumedMicros: number; approvedBudgetMicros: number }>;
  };
}

function LinesTable({ title, lines, budget = false }: { title: string; lines: Line[]; budget?: boolean }) {
  return (
    <Section title={title}>
      {lines.length === 0 ? (
        <p className="text-sm text-muted">Sin consumo.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <thead>
              <tr>
                <Th>{title.replace("Por ", "")}</Th>
                <Th className="text-right">Impresiones facturables</Th>
                <Th className="text-right">Valor CPM</Th>
                {budget ? <Th className="text-right">Presupuesto aprobado</Th> : null}
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.key}>
                  <Td>{l.label}</Td>
                  <Td className="text-right tabular-nums">{l.impressions.toLocaleString("es-HN")}</Td>
                  <Td className="text-right tabular-nums">{usd(l.valueMicros)}</Td>
                  {budget ? (
                    <Td className="text-right tabular-nums">
                      {l.approvedBudgetMicros ? usd(l.approvedBudgetMicros) : "—"}
                    </Td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Section>
  );
}

export function AdminFinance() {
  useTitle("Crédito y consumo");
  const credit = useAdmin<{ items: CreditRow[] }>("/admin/credit");
  const consumption = useAdmin<Consumption>("/admin/consumption", {}, (d) => d.run?.status === "RUNNING");
  if (credit.isLoading) return <PageLoading />;
  if (credit.error || !credit.data) return <PageError error={credit.error} />;
  const report = consumption.data?.report ?? null;
  return (
    <div className="grid gap-8">
      <PageHeader
        title="Crédito y consumo"
        description="Crédito rotativo (solo consumo CPM, nunca IA). Comprometido = consumo pendiente de pago + reservas de órdenes aún no consumidas (sin doble conteo)."
      />
      <CreditControlPanel />
      <Section title="Crédito por empresa">
        {credit.data.items.length === 0 ? (
          <EmptyState title="Ninguna empresa tiene condiciones comerciales" />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>Empresa</Th>
                  <Th>Modalidad</Th>
                  <Th className="text-right">Aprobado</Th>
                  <Th className="text-right">Comprometido</Th>
                  <Th className="text-right">Disponible</Th>
                  <Th className="text-right">Facturado / pagado / vencido</Th>
                </tr>
              </thead>
              <tbody>
                {credit.data.items.map((c) => (
                  <tr key={c.organizationId}>
                    <Td>
                      {c.legalName ?? c.organizationId}
                      {c.isTest ? <Badge className="ml-2">Prueba</Badge> : null}
                    </Td>
                    <Td>{PAYMENT_MODE_LABELS[c.paymentMode] ?? c.paymentMode}</Td>
                    <Td className="text-right tabular-nums">{usd(c.creditLimitMicros)}</Td>
                    <Td className="text-right tabular-nums">{usd(c.committedMicros)}</Td>
                    <Td className="text-right tabular-nums">{usd(c.availableMicros)}</Td>
                    <Td className="text-right text-xs text-muted">No disponible</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Section>
      {consumption.isLoading ? (
        <PageLoading />
      ) : consumption.data?.available && !report && consumption.data.run?.status === "RUNNING" ? (
        <Callout title="Midiendo el consumo" tone="info">
          Llevamos {consumption.data.run.distributions.toLocaleString("es-HN")} distribuciones. El informe aparece
          cuando la medición termine.
        </Callout>
      ) : !consumption.data?.available || !report ? (
        <Callout title="Consumo no disponible" tone="warning">
          No pudimos leer el ledger de impresiones del Ad Server en este momento.
        </Callout>
      ) : (
        <>
          <Section
            title="Consumo CPM (últimos 400 días)"
            description="Impresiones aceptadas por el Ad Server × CPM vigente."
          >
            {report.complete ? (
              <div className="flex flex-wrap gap-6 text-sm">
                <div>
                  <div className="text-xs text-muted">Impresiones facturables</div>
                  <div className="text-xl font-semibold tabular-nums">
                    {(report.impressions ?? 0).toLocaleString("es-HN")}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted">Valor devengado</div>
                  <div className="text-xl font-semibold tabular-nums">{usd(report.valueMicros)}</div>
                </div>
              </div>
            ) : (
              <Callout title="Consumo incompleto" tone="warning">
                No pudimos medir {report.unmeasuredOrganizations.length} empresas en el Ad Server. No mostramos un
                total; las tablas incluyen solo las empresas medidas por completo. Vuelve a intentarlo en unos minutos.
              </Callout>
            )}
            {report.exhausted.length > 0 ? (
              <Callout title="Campañas con presupuesto agotado" tone="danger">
                {report.exhausted.length} campañas alcanzaron su presupuesto. Revísalas en Campañas pendientes →
                Autorizadas.
              </Callout>
            ) : null}
          </Section>
          <LinesTable title="Por agencia o cliente directo" lines={report.byOrganization} />
          <LinesTable title="Por cliente" lines={report.byClient} />
          <LinesTable title="Por campaña" lines={report.byCampaign} budget />
          <LinesTable title="Por orden de compra" lines={report.byPurchaseOrder} />
        </>
      )}
    </div>
  );
}

/** Events an operator recipient can subscribe to (the catalog lives in @interactads/domain). */
const EVENTS: Record<string, string> = Object.fromEntries(
  OPERATOR_NOTIFICATION_EVENTS.map((e) => [e, NOTIFICATION_EVENTS[e].label])
);
/** Every event's label, for the notification log. */
const EVENT_LABEL = (e: string) => (e in NOTIFICATION_EVENTS ? NOTIFICATION_EVENTS[e as NotificationEvent].label : e);
interface NotificationsData {
  recipients: Array<{ email: string; events: string[] }>;
  outbox: Array<{
    id: string;
    event: string;
    recipients: number;
    subject: string;
    status: string;
    provider: string | null;
    attempts: number;
    createdAt: string;
    lastError?: string | null;
    nextAttemptAt?: string | null;
  }>;
}
interface CpmRow {
  id: string;
  organizationId: string | null;
  product: string;
  cpmMicros: number;
  effectiveFrom: string;
  reason: string;
}
const STATUS_TEXT: Record<string, string> = {
  PENDING: "Pendiente",
  SENT: "Enviado",
  SIMULATED: "Simulado (sin proveedor)",
  FAILED: "Fallido",
  NO_RECIPIENTS: "Sin destinatarios",
};

/** Puts a finally failed notification back in the queue (SUPER_ADMIN, audited). */
function RetryNotification({ id }: { id: string }) {
  const m = useAdminMutation(() => adminPost.retryNotification(id));
  return (
    <Button
      className="mt-1"
      size="sm"
      variant="ghost"
      disabled={m.isPending}
      onClick={() => {
        m.mutate(undefined, {
          onSuccess: () => toast.success("Notificación en cola nuevamente."),
          onError: () => toast.error("No se pudo reintentar."),
        });
      }}
    >
      Reintentar
    </Button>
  );
}

export function AdminSettings() {
  useTitle("Configuración");
  const roles = useOperatorRoles();
  const isSuper = roles.includes("SUPER_ADMIN");
  const notifications = useAdmin<NotificationsData>("/admin/settings/notifications");
  const cpm = useAdmin<{ items: CpmRow[] }>("/admin/cpm-rates");
  const [draft, setDraft] = useState<string | null>(null);
  const save = useAdminMutation((recipients: Array<{ email: string; events: string[] }>) =>
    adminPost.recipients(recipients)
  );
  const addRate = useAdminMutation((body: Parameters<typeof adminPost.cpm>[0]) => adminPost.cpm(body));
  if (notifications.isLoading) return <PageLoading />;
  if (notifications.error || !notifications.data) return <PageError error={notifications.error} />;
  const current = notifications.data.recipients.map((r) => `${r.email}: ${r.events.join(", ")}`).join("\n");
  const submitRecipients = () => {
    const lines = (draft ?? current)
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    const recipients = lines.map((l) => {
      const [email = "", events = ""] = l.split(":");
      const list = events
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean);
      return { email: email.trim(), events: list.length ? list : Object.keys(EVENTS) };
    });
    save.mutate(recipients, {
      onSuccess: () => {
        toast.success("Destinatarios guardados.");
        setDraft(null);
      },
      onError: (e) => toast.error(e instanceof ApiProblem ? ruleMessage(e) : "No se pudo guardar."),
    });
  };
  const submitRate = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    addRate.mutate(
      {
        organizationId: null,
        product: formField(f, "product") as "RICH_MEDIA_STANDARD",
        cpmUsd: formField(f, "cpmUsd"),
        effectiveFrom: formField(f, "effectiveFrom"),
        reason: formField(f, "reason"),
      },
      {
        onSuccess: () => toast.success("Tarifa registrada."),
        onError: (e) => toast.error(e instanceof ApiProblem ? ruleMessage(e) : "No se pudo guardar."),
      }
    );
  };
  return (
    <div className="grid gap-8">
      <PageHeader
        title="Configuración"
        description="Destinatarios de notificaciones, tarifas CPM y registro de envíos."
      />
      <Section
        title="Destinatarios administrativos"
        description="Una línea por correo: correo: eventos separados por comas (vacío = todos). El responsable asignado a cada empresa también recibe sus avisos."
      >
        <textarea
          className="min-h-28 w-full rounded-md border border-line-strong bg-surface p-2 font-mono text-xs"
          value={draft ?? current}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          disabled={!isSuper}
          aria-label="Destinatarios"
        />
        <p className="text-xs text-muted">
          Eventos:{" "}
          {Object.entries(EVENTS)
            .map(([k, v]) => `${k} (${v})`)
            .join(" · ")}
        </p>
        {isSuper ? (
          <div>
            <Button variant="primary" onClick={submitRecipients} disabled={save.isPending}>
              Guardar destinatarios
            </Button>
          </div>
        ) : null}
      </Section>
      <Section
        title="Tarifas CPM"
        description="Precio tecnológico por cada 1,000 impresiones facturables. Las tarifas no se editan: una nueva reemplaza desde su fecha."
      >
        <div className="overflow-x-auto">
          <Table>
            <thead>
              <tr>
                <Th>Alcance</Th>
                <Th>Producto</Th>
                <Th className="text-right">CPM</Th>
                <Th>Desde</Th>
                <Th>Motivo</Th>
              </tr>
            </thead>
            <tbody>
              {(cpm.data?.items ?? []).map((r) => (
                <tr key={r.id}>
                  <Td>{r.organizationId ? "Empresa" : "Global"}</Td>
                  <Td>{r.product}</Td>
                  <Td className="text-right tabular-nums">{usd(r.cpmMicros)}</Td>
                  <Td>{r.effectiveFrom}</Td>
                  <Td className="text-xs text-muted">{r.reason}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        {isSuper ? (
          <form className="grid gap-3 sm:grid-cols-4" onSubmit={submitRate}>
            <Field label="Producto">
              <select name="product" className="h-8 rounded-md border border-line-strong bg-surface px-2 text-sm">
                {["RICH_MEDIA_STANDARD", "VIDEO_RICH_MEDIA", "ADVERGAMING", "SURVEY_RICH_MEDIA"].map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </Field>
            <Field label="CPM (USD)">
              <Input name="cpmUsd" inputMode="decimal" required />
            </Field>
            <Field label="Desde">
              <Input name="effectiveFrom" type="date" required />
            </Field>
            <Field label="Motivo">
              <Input name="reason" minLength={3} required />
            </Field>
            <div className="sm:col-span-4">
              <Button type="submit" variant="secondary" disabled={addRate.isPending}>
                Registrar tarifa global
              </Button>
            </div>
          </form>
        ) : null}
      </Section>
      <Section
        title="Registro de notificaciones"
        description="Sin proveedor de correo contratado, los envíos quedan como simulados (nada sale de InteractAds)."
      >
        {notifications.data.outbox.length === 0 ? (
          <EmptyState title="Sin notificaciones" />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>Fecha</Th>
                  <Th>Evento</Th>
                  <Th>Asunto</Th>
                  <Th className="text-right">Destinatarios</Th>
                  <Th>Estado</Th>
                </tr>
              </thead>
              <tbody>
                {notifications.data.outbox.map((n) => (
                  <tr key={n.id}>
                    <Td className="whitespace-nowrap text-xs">{formatDateTime(n.createdAt)}</Td>
                    <Td className="text-xs">{EVENT_LABEL(n.event)}</Td>
                    <Td>{n.subject}</Td>
                    <Td className="text-right tabular-nums">{n.recipients}</Td>
                    <Td>
                      <Badge
                        tone={n.status === "FAILED" ? "danger" : n.status === "NO_RECIPIENTS" ? "warning" : "neutral"}
                      >
                        {STATUS_TEXT[n.status] ?? n.status}
                      </Badge>
                      {n.attempts > 0 && n.status !== "SENT" && n.status !== "SIMULATED" ? (
                        <div className="mt-1 text-xs text-muted">
                          {String(n.attempts)} intento(s)
                          {n.nextAttemptAt ? ` · reintento ${formatDateTime(n.nextAttemptAt)}` : ""}
                          {n.lastError ? ` · ${n.lastError}` : ""}
                        </div>
                      ) : null}
                      {isSuper && n.status === "FAILED" ? <RetryNotification id={n.id} /> : null}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Section>
    </div>
  );
}
